import { type ReactNode, useEffect, useRef } from "react";
import { AttachmentEditorContent } from "./attachmentEditorContent.js";
import { AttachmentEditorToolbar } from "./attachmentEditorToolbar.js";
import { useAttachmentEditorState } from "./useAttachmentEditorState.js";

interface UseAttachmentEditorProps {
  knowledgeBaseId: string;
  attachmentId: string;
  pendingPatch: { find: string; replace: string } | null;
  onPatchApplied: () => void;
  onClose: () => void;
}

export const useAttachmentEditor = (
  props: UseAttachmentEditorProps,
): { toolbar: ReactNode; content: ReactNode } => {
  const { pendingPatch, onPatchApplied } = props;
  const onClose = props.onClose;

  const {
    attachment,
    isLoading,
    isSaving,
    isInitialized,
    editedBody,
    setEditedBody,
    fetchedBody,
    hasUnsavedChanges,
    onSave,
    onDiscard,
    selectedTabIndex,
    setSelectedTabIndex,
  } = useAttachmentEditorState({
    knowledgeBaseId: props.knowledgeBaseId,
    attachmentId: props.attachmentId,
  });

  // Apply pending patch from AI assistant once content is loaded.
  // Track by reference identity so each new pendingPatch is applied exactly once.
  const appliedPatchRef = useRef<{ find: string; replace: string } | null>(
    null,
  );

  useEffect(() => {
    if (
      pendingPatch &&
      isInitialized &&
      appliedPatchRef.current !== pendingPatch
    ) {
      appliedPatchRef.current = pendingPatch;
      let body = editedBody;

      const idx = body.indexOf(pendingPatch.find);
      if (idx !== -1) {
        body =
          body.substring(0, idx) +
          pendingPatch.replace +
          body.substring(idx + pendingPatch.find.length);
      }

      setEditedBody(body);
      setSelectedTabIndex(1); // Switch to "Diff" tab
      onPatchApplied();
    }
  }, [
    pendingPatch,
    isInitialized,
    editedBody,
    onPatchApplied,
    setEditedBody,
    setSelectedTabIndex,
  ]);

  return {
    toolbar: (
      <AttachmentEditorToolbar
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSaving}
        onSave={onSave}
        onDiscard={onDiscard}
        onClose={onClose}
      />
    ),
    content: (
      <AttachmentEditorContent
        attachment={attachment}
        isLoading={isLoading}
        isSaving={isSaving}
        editedBody={editedBody}
        savedBody={fetchedBody}
        setEditedBody={setEditedBody}
        selectedTabIndex={selectedTabIndex}
        onSelectedTabIndexChange={setSelectedTabIndex}
      />
    ),
  };
};
